import React, { useEffect, useState } from 'react';

import { Box, Button, Tab, Tabs } from '@sunwisesoftware/sunwise-ui';
import { useApiIsLoaded } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import CustomTabPanel from 'common/components/segment/CustomTabPanel';
import yupResolver from 'common/utils/yupResolver';

import createProposalModal from '../../../createProposalModal';
import * as actions from '../../actions';
import * as selectors from '../../selectors';

import {
    DesignCode,
    MaxPanelLabel,
    TabGroups,
    TabModule,
    TabRooftop,
} from './components';
import validate from './validateSegment';

const initInitialValues = ({ initialValues }) => {
    const { solar_modules_number, panel } = initialValues;
    let kW = 0;

    if (panel) {
        const { watts = 0 } = panel;
        kW = (solar_modules_number * watts) / 1000;
    }

    return { ...initialValues, panel, kW };
};

const SegmentEditForm = ({
    commercialOfferId,
    fetchPreset,
    initialValues,
    mapRef,
    onCloseDrawer,
    panels,
    resetForm,
    segments,
    updateSegment,
}) => {
    const { t } = useTranslation();
    const [google, setGoogle] = useState(null);
    const apiIsLoaded = useApiIsLoaded();
    const [selectedTab, setSelectedTab] = useState('rooftop');
    const defaultValues = initInitialValues({ initialValues, panels });
    const { control, handleSubmit, reset, setValue, trigger, watch } = useForm({
        defaultValues,
        resolver: yupResolver(validate),
    });

    const presetId = watch('preset_configuration');

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    const handleOnChangeTab = (_, newValue) => setSelectedTab(newValue);

    const handleOnSave = (values) =>
        updateSegment({
            callback: () => {
                onCloseDrawer();
                resetForm();
            },
            commercialOfferId,
            google,
            mapValue: mapRef.current.self(),
            values,
        });

    useEffect(() => {
        const defaultValues = initInitialValues({
            initialValues,
            panels,
        });
        reset(defaultValues);
    }, [initialValues]);

    useEffect(() => {
        if (presetId) fetchPreset(presetId);
    }, [presetId]);

    return (
        <Box>
            <MaxPanelLabel
                control={control}
                map={mapRef.current.self()}
                segments={segments}
            />

            <DesignCode initialValues={initialValues} />

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    centered
                    onChange={handleOnChangeTab}
                    scrollButtons="auto"
                    value={selectedTab}
                    variant="fullWidth"
                >
                    <Tab
                        id="tab-rooftop"
                        key="tab-rooftop"
                        label={t('Rooftop')}
                        value="rooftop"
                    />

                    <Tab
                        id="tab-module"
                        key="tab-module"
                        label={t('Module')}
                        value="module"
                    />

                    <Tab
                        id="tab-groups"
                        key="tab-groups"
                        label={t('Groups')}
                        value="groups"
                    />
                </Tabs>
            </Box>

            <form onSubmit={handleSubmit(handleOnSave)}>
                <CustomTabPanel
                    key="tab-rooftop"
                    selectedTab={selectedTab}
                    value="rooftop"
                >
                    <TabRooftop
                        control={control}
                        google={google}
                        setValue={setValue}
                    />
                </CustomTabPanel>

                <CustomTabPanel
                    key="tab-module"
                    selectedTab={selectedTab}
                    value="module"
                >
                    <TabModule
                        control={control}
                        google={google}
                        offerPanels={panels}
                        setValue={setValue}
                        systemUid={watch('system_uid')}
                    />
                </CustomTabPanel>

                <CustomTabPanel
                    key="tab-groups"
                    selectedTab={selectedTab}
                    value="groups"
                >
                    <TabGroups control={control} trigger={trigger} />
                </CustomTabPanel>

                <Button
                    fullWidth
                    sx={{ '&.MuiButton-root': { mb: 0 } }}
                    type="submit"
                >
                    {t('Save')}
                </Button>

                <Button
                    fullWidth
                    onClick={() => resetForm()}
                    sx={{ '&.MuiButton-root': { mb: 0 } }}
                    variant="text"
                >
                    {t('Cancel')}
                </Button>
            </form>
        </Box>
    );
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    segments: selectors.getSegmentsData,
    panels: createProposalModal.selectors.getPanelsData,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPreset: (id) => dispatch(actions.fetchPreset(id)),
    resetForm: () => dispatch(actions.resetForm()),
    updateSegment: (config) => dispatch(actions.updateSegment(config)),
});

SegmentEditForm.propTypes = {
    commercialOfferId: PropTypes.string,
    fetchPreset: PropTypes.func,
    initialValues: PropTypes.object,
    mapRef: PropTypes.object,
    onCloseDrawer: PropTypes.func,
    panels: PropTypes.array,
    resetForm: PropTypes.func,
    segments: PropTypes.array,
    updateSegment: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(SegmentEditForm);
